import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, UserPlus, Search, Filter, MoreVertical, Mail, 
  Shield, Crown, Eye, Edit3, Trash2, RefreshCw, X,
  ChevronDown, Check, AlertCircle, Copy, UserCheck,
  UserX, ArrowRightLeft, Clock
} from 'lucide-react';
import { useAuth } from '../contexts/TestAuthContext';
import { useActivityLog } from '../hooks/useActivityLog';
import { api } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

interface TeamMember {
  id: string;
  email: string;
  name: string | null;
  role: string;
  role_display: string;
  role_color: string;
  status: string;
  created_at: string;
  last_active: string | null;
  last_login: string | null;
  invited_by: string | null;
}

interface TeamStats {
  total_members: number;
  active_members: number;
  pending_invites: number;
  by_role: Record<string, number>;
}

interface RoleInfo {
  value: string;
  label: string;
  description: string;
  color: string;
  permissions: string[];
}

const ROLE_ICONS: Record<string, React.ReactNode> = {
  owner: <Crown className="w-4 h-4" />,
  admin: <Shield className="w-4 h-4" />,
  analyst: <Edit3 className="w-4 h-4" />,
  viewer: <Eye className="w-4 h-4" />
};

export default function TeamManagement() {
  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [stats, setStats] = useState<TeamStats | null>(null);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [myPermissions, setMyPermissions] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDirectAddModal, setShowDirectAddModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState<TeamMember | null>(null);
  const [showRemoveModal, setShowRemoveModal] = useState<TeamMember | null>(null);
  const [showStatusModal, setShowStatusModal] = useState<TeamMember | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  
  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<{email: string; temporary_password: string} | null>(null);

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const [membersData, statsData, rolesData, permsData] = await Promise.all([
        api.get('/api/v1/team/members', { headers, showErrorToast: false }),
        api.get('/api/v1/team/stats', { headers, showErrorToast: false }),
        api.get('/api/v1/team/roles', { headers, showErrorToast: false }),
        api.get('/api/v1/team/my-permissions', { headers, showErrorToast: false })
      ]);
      
      setMembers(membersData);
      setStats(statsData);
      setRoles(rolesData.roles || []);
      setMyPermissions(permsData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtered members
  const filteredMembers = useMemo(() => {
    return members.filter(member => {
      const matchesSearch = !searchQuery || 
        member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (member.name && member.name.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesRole = roleFilter === 'all' || member.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
      
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [members, searchQuery, roleFilter, statusFilter]);

  // Invite member
  const handleInvite = async (email: string, role: string, name: string, sendEmail: boolean) => {
    try {
      setActionLoading(true);
      const token = await getToken();
      
      await api.post('/api/v1/team/invite', 
        { email, role, name, send_email: sendEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Log invite activity
      logActivity({
        actionType: 'invite',
        actionDetails: `Invited ${email} as ${role}`,
        section: 'team_management',
        metadata: { invited_email: email, role, name, email_sent: sendEmail }
      });
      
      setActionSuccess('Invitation sent successfully!');
      setShowInviteModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Direct add member
  const handleDirectAdd = async (email: string, name: string, role: string) => {
    try {
      setActionLoading(true);
      const token = await getToken();
      
      const data = await api.post('/api/v1/team/add-direct',
        { email, name, role },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCredentials(data.credentials);
      
      // Log add member activity
      logActivity({
        actionType: 'add_member',
        actionDetails: `Added ${email} as ${role}`,
        section: 'team_management',
        metadata: { added_email: email, role, name }
      });
      
      setActionSuccess('Member added successfully!');
      setShowDirectAddModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Change role
  const handleChangeRole = async (memberId: string, newRole: string) => {
    try {
      setActionLoading(true);
      const token = await getToken();
      
      await api.put(`/api/v1/team/members/${memberId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setActionSuccess('Role updated successfully!');
      setShowRoleModal(null);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Remove member
  const handleRemove = async (memberId: string) => {
    const memberToRemove = members.find(m => m.id === memberId);
    try {
      setActionLoading(true);
      const token = await getToken();
      
      await api.delete(`/api/v1/team/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Log remove member activity
      logActivity({
        actionType: 'remove_member',
        actionDetails: `Removed ${memberToRemove?.email || memberId} from team`,
        section: 'team_management',
        metadata: { removed_email: memberToRemove?.email, removed_name: memberToRemove?.name }
      });
      
      setActionSuccess('Member removed successfully!');
      setShowRemoveModal(null);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Update member status (enable/disable)
  const handleStatusChange = async (memberId: string, newStatus: string) => {
    const memberToUpdate = members.find(m => m.id === memberId);
    try {
      setActionLoading(true);
      const token = await getToken();
      
      await api.put(`/api/v1/team/members/${memberId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const statusLabel = newStatus === 'disabled' ? 'disabled' : 'enabled';
      
      // Log status change activity
      logActivity({
        actionType: 'update_role',
        actionDetails: `${statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)} ${memberToUpdate?.email || memberId}`,
        section: 'team_management',
        metadata: { member_email: memberToUpdate?.email, new_status: newStatus }
      });
      
      setActionSuccess(`Member ${statusLabel} successfully!`);
      setShowStatusModal(null);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Transfer ownership
  const handleTransferOwnership = async (newOwnerId: string, newRoleForCurrent: string) => {
    try {
      setActionLoading(true);
      const token = await getToken();
      
      const data = await api.post('/api/v1/team/transfer-ownership',
        { new_owner_id: newOwnerId, new_role_for_current: newRoleForCurrent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setActionSuccess(`Ownership transferred to ${data.new_owner_email}. Your new role is ${data.your_new_role}.`);
      setShowTransferModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Clear notifications
  useEffect(() => {
    if (actionSuccess) {
      const timer = setTimeout(() => setActionSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionSuccess]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Team Members</h2>
          <p className="text-sm text-gray-500">Manage your organization's team members and permissions</p>
        </div>
        {myPermissions?.can_invite && (
          <div className="flex gap-3">
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium text-sm"
            >
              <Mail className="w-4 h-4" />
              Invite by Email
            </button>
            <button
              onClick={() => setShowDirectAddModal(true)}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 font-medium text-sm"
            >
              <UserPlus className="w-4 h-4" />
              Add Directly
            </button>
            {myPermissions?.role === 'owner' && filteredMembers.filter(m => m.status === 'active' && m.role !== 'owner').length > 0 && (
              <button
                onClick={() => setShowTransferModal(true)}
                className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 flex items-center gap-2 font-medium text-sm"
              >
                <ArrowRightLeft className="w-4 h-4" />
                Transfer Ownership
              </button>
            )}
          </div>
        )}
      </div>

      {/* Success/Error Messages */}
      {actionSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-600" />
          <span className="text-green-800">{actionSuccess}</span>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Credentials Display (after direct add) */}
      {credentials && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-semibold text-amber-900 mb-2">New Member Credentials</h4>
          <p className="text-sm text-amber-800 mb-3">Share these credentials securely with the new team member:</p>
          <div className="bg-white rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Email:</span>
              <div className="flex items-center gap-2">
                <code className="bg-gray-100 px-2 py-1 rounded">{credentials.email}</code>
                <button 
                  onClick={() => navigator.clipboard.writeText(credentials.email)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Temporary Password:</span>
              <div className="flex items-center gap-2">
                <code className="bg-gray-100 px-2 py-1 rounded">{credentials.temporary_password}</code>
                <button 
                  onClick={() => navigator.clipboard.writeText(credentials.temporary_password)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          <button 
            onClick={() => setCredentials(null)}
            className="mt-3 text-sm text-amber-700 hover:text-amber-900"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-xs text-gray-500 font-medium">Total Members</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_members}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-xs text-gray-500 font-medium">Active</p>
            <p className="text-2xl font-bold text-green-600">{stats.active_members}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-xs text-gray-500 font-medium">Pending Invites</p>
            <p className="text-2xl font-bold text-amber-600">{stats.pending_invites}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-xs text-gray-500 font-medium">Your Role</p>
            <p className="text-lg font-bold" style={{ color: myPermissions?.role_color }}>
              {myPermissions?.role_display}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg p-4 border border-gray-200">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          {/* Role Filter */}
          <div className="relative">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="all">All Roles</option>
              <option value="owner">Owner</option>
              <option value="admin">Admin</option>
              <option value="analyst">Analyst</option>
              <option value="viewer">Viewer</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
          
          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="disabled">Disabled</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
          
          {/* Refresh */}
          <button
            onClick={fetchData}
            className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    {searchQuery || roleFilter !== 'all' || statusFilter !== 'all' 
                      ? 'No members match your filters'
                      : 'No team members yet. Invite your first team member!'}
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member.id} className={`hover:bg-gray-50 ${member.status === 'disabled' ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                          member.status === 'disabled' 
                            ? 'bg-gray-400' 
                            : 'bg-gradient-to-br from-blue-500 to-purple-600'
                        }`}>
                          {(member.name || member.email)[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{member.name || 'No name'}</p>
                          <p className="text-xs text-gray-500">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ 
                          backgroundColor: `${member.role_color}15`,
                          color: member.role_color
                        }}
                      >
                        {ROLE_ICONS[member.role]}
                        {member.role_display}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        member.status === 'active' ? 'bg-green-100 text-green-700' :
                        member.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {member.status === 'active' && <UserCheck className="w-3 h-3" />}
                        {member.status === 'pending' && <Mail className="w-3 h-3" />}
                        {member.status === 'disabled' && <UserX className="w-3 h-3" />}
                        {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {member.last_login ? (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-gray-400" />
                          {formatRelativeTime(member.last_login)}
                        </div>
                      ) : (
                        <span className="text-gray-400">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {myPermissions?.can_change_roles && member.role !== 'owner' && member.status !== 'pending' && (
                          <button
                            onClick={() => setShowRoleModal(member)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Change role"
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                        )}
                        {myPermissions?.can_remove && member.role !== 'owner' && member.status === 'active' && (
                          <button
                            onClick={() => setShowStatusModal(member)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                            title="Disable member"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}
                        {myPermissions?.can_remove && member.role !== 'owner' && member.status === 'disabled' && (
                          <button
                            onClick={() => handleStatusChange(member.id, 'active')}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                            title="Enable member"
                          >
                            <UserCheck className="w-4 h-4" />
                          </button>
                        )}
                        {myPermissions?.can_remove && member.role !== 'owner' && (
                          <button
                            onClick={() => setShowRemoveModal(member)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Remove member"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination info */}
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-500">
            Showing {filteredMembers.length} of {members.length} members
          </p>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteModal
          roles={roles}
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInvite}
          loading={actionLoading}
        />
      )}

      {/* Direct Add Modal */}
      {showDirectAddModal && (
        <DirectAddModal
          roles={roles}
          onClose={() => setShowDirectAddModal(false)}
          onAdd={handleDirectAdd}
          loading={actionLoading}
        />
      )}

      {/* Change Role Modal */}
      {showRoleModal && (
        <ChangeRoleModal
          member={showRoleModal}
          roles={roles}
          onClose={() => setShowRoleModal(null)}
          onChangeRole={handleChangeRole}
          loading={actionLoading}
        />
      )}

      {/* Remove Confirmation Modal */}
      {showRemoveModal && (
        <RemoveModal
          member={showRemoveModal}
          onClose={() => setShowRemoveModal(null)}
          onRemove={handleRemove}
          loading={actionLoading}
        />
      )}

      {/* Disable/Enable Status Modal */}
      {showStatusModal && (
        <StatusModal
          member={showStatusModal}
          onClose={() => setShowStatusModal(null)}
          onChangeStatus={handleStatusChange}
          loading={actionLoading}
        />
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <TransferOwnershipModal
          members={filteredMembers.filter(m => m.status === 'active' && m.role !== 'owner')}
          roles={roles}
          onClose={() => setShowTransferModal(false)}
          onTransfer={handleTransferOwnership}
          loading={actionLoading}
        />
      )}
    </div>
  );
}


// ============== Modal Components ==============

function InviteModal({ roles, onClose, onInvite, loading }: {
  roles: RoleInfo[];
  onClose: () => void;
  onInvite: (email: string, role: string, name: string, sendEmail: boolean) => void;
  loading: boolean;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('analyst');
  const [sendEmail, setSendEmail] = useState(true);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Invite Team Member</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="colleague@company.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="John Smith"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {roles.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {roles.find(r => r.value === role) && (
              <p className="text-xs text-gray-500 mt-1">
                {roles.find(r => r.value === role)?.description}
              </p>
            )}
          </div>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Send invitation email</span>
          </label>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onInvite(email, role, name, sendEmail)}
            disabled={!email || loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send Invite
          </button>
        </div>
      </div>
    </div>
  );
}

function DirectAddModal({ roles, onClose, onAdd, loading }: {
  roles: RoleInfo[];
  onClose: () => void;
  onAdd: (email: string, name: string, role: string) => void;
  loading: boolean;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('analyst');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Add Team Member Directly</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-amber-800">
            This will create an account and generate temporary credentials that you&apos;ll need to share with the user.
          </p>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="John Smith"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="john@company.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {roles.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onAdd(email, name, role)}
            disabled={!email || !name || loading}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Add Member
          </button>
        </div>
      </div>
    </div>
  );
}

function ChangeRoleModal({ member, roles, onClose, onChangeRole, loading }: {
  member: TeamMember;
  roles: RoleInfo[];
  onClose: () => void;
  onChangeRole: (memberId: string, role: string) => void;
  loading: boolean;
}) {
  const [newRole, setNewRole] = useState(member.role);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Change Role</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-gray-600">
            Change role for <strong>{member.name || member.email}</strong>
          </p>
        </div>
        
        <div className="space-y-2">
          {roles.map(r => (
            <label 
              key={r.value}
              className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                newRole === r.value 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="role"
                value={r.value}
                checked={newRole === r.value}
                onChange={(e) => setNewRole(e.target.value)}
                className="sr-only"
              />
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${r.color}20`, color: r.color }}>
                {ROLE_ICONS[r.value]}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{r.label}</p>
                <p className="text-xs text-gray-500">{r.description}</p>
              </div>
              {newRole === r.value && (
                <Check className="w-5 h-5 text-blue-600" />
              )}
            </label>
          ))}
        </div>
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onChangeRole(member.id, newRole)}
            disabled={newRole === member.role || loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            Update Role
          </button>
        </div>
      </div>
    </div>
  );
}

function RemoveModal({ member, onClose, onRemove, loading }: {
  member: TeamMember;
  onClose: () => void;
  onRemove: (memberId: string) => void;
  loading: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Remove Team Member</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-800">
            Are you sure you want to remove <strong>{member.name || member.email}</strong> from the team?
          </p>
          <p className="text-sm text-red-600 mt-2">
            They will lose access to all claims and reports.
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onRemove(member.id)}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Remove Member
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusModal({ member, onClose, onChangeStatus, loading }: {
  member: TeamMember;
  onClose: () => void;
  onChangeStatus: (memberId: string, status: string) => void;
  loading: boolean;
}) {
  const isDisabling = member.status === 'active';
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">
            {isDisabling ? 'Disable Team Member' : 'Enable Team Member'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className={`${isDisabling ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'} border rounded-lg p-4 mb-4`}>
          <p className={isDisabling ? 'text-amber-800' : 'text-green-800'}>
            {isDisabling 
              ? <>Are you sure you want to disable <strong>{member.name || member.email}</strong>?</>
              : <>Re-enable access for <strong>{member.name || member.email}</strong>?</>
            }
          </p>
          <p className={`text-sm mt-2 ${isDisabling ? 'text-amber-600' : 'text-green-600'}`}>
            {isDisabling 
              ? 'They will not be able to log in until re-enabled. Their data will be preserved.'
              : 'They will regain access to the team with their previous role.'
            }
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onChangeStatus(member.id, isDisabling ? 'disabled' : 'active')}
            disabled={loading}
            className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 ${
              isDisabling 
                ? 'bg-amber-600 hover:bg-amber-700' 
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : (isDisabling ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />)}
            {isDisabling ? 'Disable Member' : 'Enable Member'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TransferOwnershipModal({ members, roles, onClose, onTransfer, loading }: {
  members: TeamMember[];
  roles: RoleInfo[];
  onClose: () => void;
  onTransfer: (newOwnerId: string, newRoleForCurrent: string) => void;
  loading: boolean;
}) {
  const [selectedMember, setSelectedMember] = useState<string>('');
  const [newRoleForCurrent, setNewRoleForCurrent] = useState('admin');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Transfer Ownership</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <p className="text-amber-800 font-medium">⚠️ This action cannot be undone by you</p>
          <p className="text-sm text-amber-600 mt-1">
            The new owner will have full control over the team and can change your role.
          </p>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select New Owner</label>
            <select
              value={selectedMember}
              onChange={(e) => setSelectedMember(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choose a team member...</option>
              {members.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name || m.email} ({m.role_display})
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your New Role After Transfer</label>
            <select
              value={newRoleForCurrent}
              onChange={(e) => setNewRoleForCurrent(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="admin">Admin</option>
              <option value="analyst">Analyst</option>
              <option value="viewer">Viewer</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              You will become a {newRoleForCurrent} after transferring ownership.
            </p>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onTransfer(selectedMember, newRoleForCurrent)}
            disabled={!selectedMember || loading}
            className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
            Transfer Ownership
          </button>
        </div>
      </div>
    </div>
  );
}

